// 261030 — logic THUẦN (không import antd) cho màn «Tạo plan phim mới»: chương nào còn chọn được, mặc định chọn gì, bật/tắt chọn nhiều.
// Quy tắc: chương đã nằm trong một plan CÒN TỒN TẠI của truyện (`plan.sourceChapterIds`) thì không hiện trong danh sách; xoá plan ⇒ chương hiện lại.

export const CREATE_CHAPTER_TEXT = {
  allUsed: 'Mọi chương đã có plan.',
  allUsedHint: 'Muốn làm lại chương nào thì xoá plan chứa chương đó ở danh sách plan, chương sẽ hiện lại ở đây.',
  multiToggleOn: 'Chọn nhiều chương',
  multiToggleOff: 'Chỉ chọn một chương',
  plansLoadFailed: 'Không đọc được danh sách plan',
} as const;

interface ChapterLike { id: string; orderIndex: number }
interface PlanLike { sourceChapterIds?: string[] | null }

/** Mọi chương đã nằm trong một plan nào đó. */
export function usedChapterIdsOf(plans: ReadonlyArray<PlanLike> | null | undefined): Set<string> {
  return new Set((plans ?? []).flatMap((p) => p.sourceChapterIds ?? []));
}

/** Chương còn chọn được, giữ thứ tự `orderIndex`. */
export function availableChaptersOf<C extends ChapterLike>(chapters: ReadonlyArray<C>, plans: ReadonlyArray<PlanLike> | null | undefined): C[] {
  const used = usedChapterIdsOf(plans);
  return chapters.filter((c) => !used.has(c.id)).sort((a, b) => a.orderIndex - b.orderIndex);
}

/**
 * Lựa chọn ban đầu. Mặc định MỘT chương = chương đầu tiên chưa có plan. `?chapters=` (từ plan vừa xoá) chỉ được nhận phần còn chọn được;
 * ≥2 chương hợp lệ ⇒ mở sẵn chế độ chọn nhiều để không âm thầm bỏ bớt chương người dùng mang theo.
 */
export function initialCreateSelectionOf(available: ReadonlyArray<ChapterLike>, requestedIds: ReadonlyArray<string> = []): { ids: string[]; multi: boolean } {
  const ok = new Set(available.map((c) => c.id));
  const requested = available.filter((c) => requestedIds.includes(c.id) && ok.has(c.id)).map((c) => c.id);
  if (requested.length >= 2) return { ids: requested, multi: true };
  if (requested.length === 1) return { ids: requested, multi: false };
  return { ids: available.length ? [available[0].id] : [], multi: false };
}

/** Bấm một chương: chế độ đơn ⇒ chọn đúng chương đó; chế độ nhiều ⇒ bật/tắt chương đó. */
export function toggleChapterOf(selected: ReadonlyArray<string>, id: string, multi: boolean): string[] {
  if (!multi) return [id];
  return selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
}

/** Tắt chế độ chọn nhiều ⇒ giữ lại đúng một chương (chương đầu theo thứ tự `available`). */
export function collapseToSingleOf(selected: ReadonlyArray<string>, available: ReadonlyArray<ChapterLike>): string[] {
  const first = available.find((c) => selected.includes(c.id));
  return first ? [first.id] : [];
}

/** Hai nút chọn engine của màn tạo plan (luôn hiện, không nằm trong mục gấp). */
export const CREATE_ENGINE_CHOICES = [
  { value: 'SEEDANCE_20', label: 'Seedance 2.0', hint: 'model mini, rẻ hơn' },
  { value: 'SEEDANCE_25', label: 'Seedance 2.5', hint: 'khuyến nghị' },
] as const;

/** Câu lỗi khi xoá plan: ưu tiên câu tiếng Việt của BE (`data.message` hoặc `data.messages[]`), không để lộ «Request failed with status code 409». */
export function deletePlanErrorTextOf(err: any): string {
  const d = err?.response?.data;
  const fromServer = String(d?.message || (Array.isArray(d?.messages) ? d.messages.filter(Boolean).join(' · ') : '') || '');
  if (fromServer) return fromServer;
  const status = err?.response?.status;
  if (status === 404) return 'Plan này đã không còn — danh sách sẽ được tải lại.';
  if (status === 409) return 'Plan đang có việc chạy nền hoặc cần xử lý tay — chưa xoá được.';
  return `Không xoá được plan: ${err?.message || 'lỗi không rõ'}`;
}
